<template>
  <div class="page">
    <SiteNav />
    <main id="main" tabindex="-1">
      <section class="hero" aria-labelledby="hero-title">
        <div class="container hero__layout">
          <div class="hero__copy">
            <p class="eyebrow">Allading messenger</p>
            <h1 id="hero-title">
              Your people.<br /><span>Your conversations.</span>
            </h1>
            <p class="hero__lede">
              A message, a voice note, a familiar face. Keep your everyday
              conversations together, with secure chats when privacy matters
              most.
            </p>
            <StoreButtons tone="light" />
            <a class="text-link" href="#experience"
              >Take a look inside <span aria-hidden="true">↓</span></a
            >
            <div class="hero__facts">
              <span>Chats &amp; voice notes</span
              ><span>Voice &amp; video calls</span><span>Two chat modes</span>
            </div>
          </div>
          <div class="hero__visual" aria-label="Allading app previews">
            <img
              class="hero__secondary screenshot"
              :src="conversation"
              alt="Conversation with text, a voice message and call history"
              width="853"
              height="1844"
            />
            <img
              class="hero__primary screenshot"
              :src="inbox"
              alt="Allading chat list with unread and group filters"
              width="852"
              height="1846"
              fetchpriority="high"
            />
            <span class="hero__caption">Less distance. More conversation.</span>
          </div>
        </div>
      </section>
      <section
        id="experience"
        class="experience"
        aria-labelledby="experience-title"
      >
        <div class="container">
          <div class="section-head">
            <p class="eyebrow">Inside Allading</p>
            <h2 id="experience-title">
              From a quick hello<br />to a longer catch-up.
            </h2>
            <p>
              Chats, messages, calls and settings. A closer look at your
              everyday conversations.
            </p>
          </div>
          <div class="screen-gallery">
            <figure
              v-for="screen in screens"
              :key="screen.title"
              class="screen-story"
            >
              <img
                class="screenshot"
                :src="screen.image"
                :alt="screen.alt"
                :width="screen.width"
                :height="screen.height"
                loading="lazy"
                decoding="async"
              />
              <figcaption>
                <span class="screen-story__label">{{ screen.label }}</span>
                <h3>{{ screen.title }}</h3>
                <p>{{ screen.text }}</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
      <section id="features" class="features" aria-labelledby="features-title">
        <div class="container">
          <div class="section-head">
            <p class="eyebrow">Made for everyday connection</p>
            <h2 id="features-title">Everything close at hand.</h2>
          </div>
          <ul class="feature-grid">
            <li v-for="feature in features" :key="feature.title">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.text }}</p>
            </li>
          </ul>
        </div>
      </section>
      <section id="security" class="security" aria-labelledby="security-title">
        <div class="container">
          <div class="section-head">
            <p class="eyebrow">Privacy, with a clear choice</p>
            <h2 id="security-title">
              Different conversations.<br />Different ways to keep them.
            </h2>
            <p>
              Choose the chat mode that fits your conversation. Secure chats and
              ordinary chats handle history differently.
            </p>
          </div>
          <div class="mode-grid">
            <article>
              <span class="mode-label">Secure chat</span>
              <h3>Between your devices.</h3>
              <p>
                Messages are end-to-end encrypted using the Signal Protocol. The
                server relays encrypted content and keeps it for a limited time.
                Compare safety numbers or QR codes with your contact to verify
                the connection.
              </p>
              <span class="mode-note"
                >For private, device-based conversations</span
              >
            </article>
            <article>
              <span class="mode-label">Ordinary chat</span>
              <h3>History that comes with you.</h3>
              <p>
                Keep your conversation history in the cloud and restore it when
                you sign in on another device. This mode uses server-managed
                encryption and is also used for group chats; it is not
                end-to-end encrypted.
              </p>
              <span class="mode-note"
                >For cloud history and group conversations</span
              >
            </article>
          </div>
          <div class="security-notes">
            <div>
              <h3>A backup you control</h3>
              <p>
                Enable a PIN-protected encrypted key backup in Settings. Keep
                your PIN safe so you can restore your keys.
              </p>
            </div>
            <div>
              <h3>A clear transport policy</h3>
              <p>
                Cloud connectivity is the main way to send messages and make
                calls. Android Bluetooth and Wi-Fi mesh tools are diagnostic
                features, not a guaranteed offline messaging service.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section class="trust-strip" aria-labelledby="trust-title">
        <div class="container trust-strip__layout">
          <div>
            <p class="eyebrow">Privacy &amp; terms</p>
            <h2 id="trust-title">Your choices, explained.</h2>
            <p>
              Read how chat modes work, review the service rules or request
              account deletion. Find the details before you share.
            </p>
          </div>
          <nav aria-label="Privacy and account controls">
            <router-link to="/legal"
              >Explore privacy &amp; terms
              <span aria-hidden="true">↗</span></router-link
            ><router-link to="/privacy"
              >How we handle information
              <span aria-hidden="true">→</span></router-link
            ><router-link to="/delete-account"
              >Delete your account
              <span aria-hidden="true">→</span></router-link
            >
          </nav>
        </div>
      </section>
      <SiteFaq />
      <section id="download" class="download" aria-labelledby="download-title">
        <div class="container">
          <p class="eyebrow">Take the conversation with you</p>
          <h2 id="download-title">Say hello on Allading.</h2>
          <p>
            Create your profile, find your contacts and choose how you chat.
            Register with your email and a password, then verify your email with
            a code.
          </p>
          <StoreButtons tone="dark" />
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
</template>
<script setup lang="ts">
import SiteFaq from "@/components/site/SiteFaq.vue";
import SiteNav from "@/components/site/SiteNav.vue";
import SiteFooter from "@/components/site/SiteFooter.vue";
import StoreButtons from "@/components/site/StoreButtons.vue";
import inbox from "@/assets/1.webp";
import conversation from "@/assets/2.webp";
import settings from "@/assets/3.webp";
import call from "@/assets/4.webp";
const screens = [
  {
    label: "Chats",
    title: "Find your conversation.",
    image: inbox,
    width: 852,
    height: 1846,
    alt: "Chat list with search, unread badges and group filters",
    caption: "Your chats, organised in one place.",
    text: "Search your chats, filter unread messages and find your groups. Preview the latest message before opening a conversation.",
  },
  {
    label: "Messages",
    title: "More than a line of text.",
    image: conversation,
    width: 853,
    height: 1844,
    alt: "Conversation with messages, a voice recording and grouped call history",
    caption: "Text, voice notes and call history together.",
    text: "Send text, photos and voice notes. Reply, react and edit your own text messages, with call summaries kept in the conversation.",
  },
  {
    label: "Calls",
    title: "Hear a familiar voice.",
    image: call,
    width: 853,
    height: 1844,
    alt: "Outgoing voice call with video, mute and end call controls",
    caption: "Voice and video, right from your chat.",
    text: "Start a voice or video call. Control your microphone, switch audio output and turn video on when you want to be seen.",
  },
  {
    label: "Settings",
    title: "Make it yours.",
    image: settings,
    width: 591,
    height: 1280,
    alt: "Settings showing PIN backup, privacy information and mesh diagnostics",
    caption: "Backup and privacy controls within reach.",
    text: "Manage your encrypted key backup, review privacy and security information, and explore the in-app guide.",
  },
];
const features = [
  {
    title: "Direct & group chats",
    text: "Keep one-to-one conversations and group discussions together. Groups use ordinary cloud history.",
  },
  {
    title: "Voice notes & photos",
    text: "Record a voice message when typing is not enough, or share a photo from your phone.",
  },
  {
    title: "Voice & video calls",
    text: "Call from a conversation and see the outcome in your chat history. Calls require an internet connection.",
  },
  {
    title: "Contact requests",
    text: "Find people and review incoming contact requests before adding them to your contacts.",
  },
  {
    title: "Message controls",
    text: "Reply, react, edit your own text and delete your messages. Deleted messages leave a marker in the conversation.",
  },
  {
    title: "Send when reconnected",
    text: "An outbox keeps pending messages and retries delivery when your connection returns.",
  },
];
</script>
<style scoped>
.trust-strip {
  padding: 64px 0;
  background: #f6f5fb;
}
.trust-strip__layout {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 64px;
  align-items: center;
}
.trust-strip h2 {
  font-size: clamp(30px, 3.5vw, 44px);
  margin: 16px 0;
}
.trust-strip p:not(.eyebrow) {
  color: var(--text-secondary);
  max-width: 570px;
}
.trust-strip nav {
  display: flex;
  flex-direction: column;
}
.trust-strip a {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid #ded9ed;
  color: #6551a5;
  font-size: 14px;
}
.trust-strip a:hover {
  color: #362451;
}
@media (max-width: 767px) {
  .trust-strip__layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
.page {
  overflow-x: clip;
}
h1,
h2,
h3 {
  font-family: var(--font-display);
  font-weight: 500;
  letter-spacing: -0.045em;
  text-wrap: balance;
}
h2 {
  font-size: clamp(34px, 4.4vw, 60px);
  line-height: 1.1;
}
h3 {
  font-size: 25px;
  line-height: 1.2;
}
.hero {
  background: #111326;
  color: #fff;
  padding: calc(var(--nav-h) + 72px) 0 64px;
  position: relative;
}
.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 85% 40%, #39325b 0, transparent 60%);
  pointer-events: none;
}
.hero__layout {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
.hero__copy {
  max-width: 620px;
}
.hero .eyebrow {
  color: #c1bbff;
}
h1 {
  font-size: clamp(48px, 5.6vw, 82px);
  line-height: 1.04;
  margin: 24px 0;
}
h1 span {
  color: #b3a8ff;
}
.hero__lede {
  font-size: 19px;
  line-height: 1.7;
  color: #d0d0de;
  max-width: 510px;
  margin-bottom: 32px;
}
.hero__copy :deep(.stores) {
  justify-content: flex-start;
}
.text-link {
  display: inline-flex;
  gap: 24px;
  margin-top: 28px;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #ffffff40;
}
.hero__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  margin-top: 42px;
  font-size: 12px;
  color: #bcbccb;
}
.hero__facts span::before {
  content: "·";
  color: #b3a8ff;
  margin-right: 8px;
}
.hero__visual {
  position: relative;
  min-height: 680px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 8px 36px;
}
.screenshot {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 20px;
  border: 1px solid #ffffff26;
  box-shadow: 0 24px 64px #0003;
}
.hero__primary {
  width: 57%;
  position: relative;
  z-index: 2;
}
.hero__secondary {
  width: 49%;
  position: absolute;
  left: 0;
  top: 58px;
  transform: rotate(-7deg);
  opacity: 0.88;
}
.hero__caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #c1bbd9;
  letter-spacing: 0.04em;
}
.experience,
.features,
.security,
.download {
  padding: var(--section-y) 0;
}
.section-head {
  max-width: 820px;
  margin-bottom: 56px;
}
.section-head h2 {
  margin-top: 20px;
}
.section-head > p:last-child:not(.eyebrow) {
  max-width: 650px;
  margin-top: 24px;
  font-size: 18px;
  color: var(--text-secondary);
}
.experience {
  background: #f6f5fb;
}
.screen-gallery {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 32px;
  align-items: start;
}
.screen-story {
  margin: 0;
  min-width: 0;
}
.screen-story:nth-child(even) {
  padding-top: 64px;
}
.screen-story .screenshot {
  border-radius: 12px;
  box-shadow: 0 16px 36px #17182d16;
}
.screen-story figcaption {
  padding-top: 28px;
}
.screen-story__label {
  display: block;
  color: #6551a5;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.screen-story h3 {
  font-size: 24px;
  line-height: 1.25;
}
.screen-story p {
  margin-top: 14px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.7;
}
.feature-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px 36px;
}
.feature-grid li {
  border-top: 1px solid #dedbe9;
  padding-top: 24px;
}
.feature-grid p {
  color: var(--text-secondary);
  margin-top: 14px;
  max-width: 360px;
}
.security {
  background: #17182d;
  color: #fff;
}
.security .eyebrow {
  color: #c1bbff;
}
.security .section-head > p:last-child {
  color: #c7c6d9;
}
.mode-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.mode-grid article {
  border-top: 1px solid #ffffff40;
  padding: 32px 0 0;
}
.mode-grid {
  gap: 64px;
}
.mode-label {
  color: #c1bbff;
  font-size: 13px;
}
.mode-grid h3 {
  font-size: 32px;
  margin-top: 20px;
}
.mode-grid p {
  color: #d0cedf;
  margin-top: 20px;
}
.mode-note {
  display: block;
  border-top: 1px solid #ffffff26;
  padding-top: 20px;
  margin-top: 32px;
  color: #c1bbff;
  font-size: 12px;
}
.security-notes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  margin-top: 56px;
}
.security-notes h3 {
  font-size: 21px;
}
.security-notes p {
  color: #c7c6d9;
  margin-top: 12px;
  font-size: 14px;
}
.download {
  text-align: center;
  background: #eeebfa;
}
.download h2 {
  margin: 20px 0;
  font-size: clamp(40px, 5vw, 68px);
}
.download p:not(.eyebrow) {
  max-width: 580px;
  margin: 0 auto 32px;
  color: var(--text-secondary);
}
@media (min-width: 1500px) {
  .hero__visual {
    min-height: 760px;
  }
  .hero__primary {
    max-width: 340px;
  }
  .hero__secondary {
    max-width: 300px;
    left: 40px;
  }
}
@media (max-width: 1000px) {
  .hero__layout {
    gap: 24px;
  }
  .hero__visual {
    min-height: 540px;
  }
  .hero__secondary {
    top: 80px;
  }
  .hero__facts {
    margin-top: 28px;
  }
  .screen-gallery {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 48px 40px;
  }
  .feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 767px) {
  .hero {
    padding-top: calc(var(--nav-h) + 40px);
  }
  .hero__layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .hero__copy {
    max-width: 540px;
  }
  .hero__lede {
    font-size: 17px;
  }
  .hero__visual {
    width: min(460px, 100%);
    min-height: 0;
    justify-self: center;
    padding-bottom: 40px;
  }
  .hero__primary {
    width: 56%;
  }
  .hero__secondary {
    top: 24px;
    left: 8px;
  }
  .screen-gallery {
    gap: 40px 24px;
  }
  .screen-story:nth-child(even) {
    padding-top: 40px;
  }
  .screen-story h3 {
    font-size: 21px;
  }
  .screen-story p {
    font-size: 13px;
  }
  .section-head {
    margin-bottom: 36px;
  }
  .feature-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .mode-grid,
  .security-notes {
    grid-template-columns: 1fr;
  }
  .mode-grid article {
    padding: 28px 0 0;
  }
  .security-notes {
    gap: 28px;
  }
  .hero__facts {
    gap: 8px 14px;
  }
}
@media (max-width: 480px) {
  .screen-gallery {
    grid-template-columns: 1fr;
    gap: 56px;
  }
  .screen-story:nth-child(even) {
    padding-top: 0;
  }
  .screen-story {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
  }
  .screen-story .screenshot {
    width: min(280px, 84%);
    justify-self: center;
  }
  .screen-story figcaption {
    max-width: 340px;
    margin: 0 auto;
    width: 100%;
  }
}
</style>
